import { useEffect, useState } from 'react';

/**
 * `useOnlineStatus()` returns:
 *   { online }                — current `navigator.onLine` value.
 *   { justReconnected }       — true for ~2.5s after we cross from
 *                              offline to online, then auto-clears.
 *
 * Drives the OnlineIndicator chip and any future toast about
 * regained connectivity. Safe on SSR (defaults to online).
 */
export function useOnlineStatus(): { online: boolean; justReconnected: boolean } {
  const initial = typeof navigator === 'undefined' ? true : navigator.onLine;
  const [online, setOnline] = useState(initial);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    let timer: number | undefined;

    const onOnline = () => {
      setOnline(true);
      setJustReconnected(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setJustReconnected(false), 2500);
    };
    const onOffline = () => {
      setOnline(false);
      setJustReconnected(false);
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.clearTimeout(timer);
    };
  }, []);

  return { online, justReconnected };
}
